import {
  AutoComplete,
  AutoCompleteMulti,
  DateInput,
  FormInput,
  ISelectType,
  RadioGroupInput,
  SwitchInput,
  useFetch,
  useFormContext,
} from "@4uhub/lib4uhub";
import { Box, Grid, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SerializedListService from "../../../../../../../services/serializedList.service";
import { tasyScheduleRecordList } from "../../../../../../../services/tasyScheduleRecord.service";
import { useAutomationRules } from "../../../../../../../store/contexts/AutomationRulesContext";
import { Actions } from "./Actions/Actions";
import { TMessageForm } from "./messageSchema";
import { SelectEstablishment } from "./SelectEstablishment";
import { SelectEvaluationForm } from "./SelectEvaluationForm";
import { listMainGenerics } from "../../../../../../../services/generic.service";
import { OriginsField } from "./OriginsField";
import { SchedulingMethodField } from "./SchedulingMethodField";
import { ServiceClassificationField } from "./ServiceClassificationField";
import { ServiceSectorField } from "./ServiceSectorField";
import { ServiceTypeField } from "./ServiceTypeField";
import { ServiceComplaintField } from "./ServiceComplaintField";
import { GroupingIdentifierField } from "./GroupingIdentifierField";
import { IntegrationTypeField } from "./IntegrationTypeField";
import { InternalProcedureField } from "./InternalProcedureField";
import { IndividualIdentifierField } from "./IndividualIdentifierField";
import { normalizeNumber } from "../../../../../../../utils/normalizeQtyAttempts";
import { calendarPreferenceList } from "../../../../../../../services/calendarPreference.service";
import { getIsRequiredCancellation } from "../../../../../../../services/ticketAutomationMessageRules.service";
import { listAppointmentArrivalRules } from "../../../../../../../services/ticketAutomationMessageRules.service";
import { useParams } from "react-router-dom";
import { TasyAuthField } from "./TasyAuthField";
import { TasyCalendarStatusesField } from "./TasyCalendarStatusesField";
import { eSignRuleList } from "../../../../../../../services/esignRule.service";
import {
  REAGENDAMENTO_AUTOMATION_CODES,
  TASY_NPS_SATISFACTION_SURVEY_AUTOMATION_CODE,
  isReagendamentoAutomationCode,
} from "../../../../../../../utils/ticketAutomation";
import { SurgeryTypeField } from "./SurgeryTypeField";
import { SurgeryPorteField } from "./SurgeryPorteField";
import { SurgeryCareCharacterField } from "./SurgeryCareCharacterField";
import { SurgeryServiceSectorField } from "./SurgeryServiceSectorField";
import { SurgeryHealthInsuranceField } from "./SurgeryHealthInsuranceField";

const TASY_SCHEDULE_CLASSIFICATIONS_ROUTE =
  process.env.REACT_APP_AUTOMATIONS + "/api/v1/TasyScheduleClassification/";
const tasyScheduleClassificationsService = new SerializedListService(
  TASY_SCHEDULE_CLASSIFICATIONS_ROUTE,
);

const TASY_SCHEDULE_TYPES_ROUTE =
  process.env.REACT_APP_AUTOMATIONS + "/api/v1/TasyScheduleType/";
const tasyScheduleTypesService = new SerializedListService(
  TASY_SCHEDULE_TYPES_ROUTE,
);

const TASY_HEALTH_INSURANCES_ROUTE =
  process.env.REACT_APP_AUTOMATIONS + "/api/v1/TasyHealthInsurance/";
const tasyHealthInsurancesService = new SerializedListService(
  TASY_HEALTH_INSURANCES_ROUTE,
);

const translationPath = "components.messages.";

const AUTOMATION_CODES_WITH_ESTABLISHMENTS_AND_INSURANCES = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "9",
  "10",
  "11",
  "12",
  "13",
  "14",
  "16",
  "17",
  "18",
  "22",
  "23",
  "24",
  "27",
  "34",
  "35",
  ...REAGENDAMENTO_AUTOMATION_CODES,
];

const AUTOMATION_CODES_WITH_SCHEDULE_RECORDS = [
  "1",
  "2",
  "4",
  "5",
  "6",
  "9",
  "10",
  "11",
  "12",
  "13",
  "14",
  "16",
  "17",
  "18",
  "22",
  "23",
  "24",
  "34",
  ...REAGENDAMENTO_AUTOMATION_CODES,
];

const AUTOMATION_CODES_WITH_PREFERENCES = [
  "1",
  "5",
  "11",
  "12",
  "13",
  "16",
  "17",
  "18",
  "22",
  "23",
  "24",
  "34",
  ...REAGENDAMENTO_AUTOMATION_CODES,
];

const AUTOMATION_CODES_WITH_CLASSIFICATIONS = [
  "1",
  "2",
  "5",
  "6",
  "9",
  "10",
  "11",
  "12",
  "13",
  "14",
  "16",
  "17",
  "18",
  "22",
  "23",
  "24",
  "34",
  ...REAGENDAMENTO_AUTOMATION_CODES,
];

export const MessageFields = memo(({ idMessage }: { idMessage?: string }) => {
  const { t } = useTranslation();

  const { id } = useParams();

  const { automation, ruleTemplate } = useAutomationRules();

  const { formState, setValue, getValues, watch } =
    useFormContext<TMessageForm>();

  const [optionsScheduleRecords, setOptionsScheduleRecords] = useState<
    ISelectType[]
  >([]);
  const [optionsPreferences, setOptionsPreferences] = useState<ISelectType[]>(
    [],
  );
  const [missingStatusOptions, setMissingStatusOptions] = useState<
    ISelectType[]
  >([]);

  const { sendRequest } = useFetch(tasyScheduleRecordList);

  const { sendRequest: calendarPreferenceRequest } = useFetch(
    calendarPreferenceList,
  );
  const { sendRequest: isRequiredCancellationRequest } = useFetch(
    getIsRequiredCancellation,
  );
  const { sendRequest: missingStatusRequest } = useFetch(
    listMainGenerics as (params: { Identifier: string }) => any,
  );
  const missingStatusFetchedRef = useRef(false);
  const automationCode = useMemo(
    () => (automation && automation.code ? automation.code : null),
    [automation],
  );
  const showMissingStatus = useMemo(
    () => isReagendamentoAutomationCode(automationCode),
    [automationCode],
  );
  const showTasyNpsSatisfactionSurveyFields =
    String(automationCode) === TASY_NPS_SATISFACTION_SURVEY_AUTOMATION_CODE;

  const exceptionError = !!formState?.errors?.exception;
  const exceptionValue = !!watch("exception");

  const tasyScheduleTypeIntegrationId = useMemo(() => {
    if (!automationCode) return;

    switch (automationCode) {
      case "1":
      case "2":
      case "9":
      case "16":
        return [3];
      case "4":
      case "14":
      case "22":
      case "23":
      case "24":
      case "34":
        return [1];
      case "5":
      case "6":
      case "10":
      case "17":
        return [2];
      case "11":
      case "18":
        return [2, 3];
      case "31":
      case "32":
        return [2, 3];
      case "12":
        return [3];
      case "13":
        return [2];
      default:
        return null;
    }
  }, [automationCode]);

  const params = useMemo(() => {
    return { tasyScheduleTypeIntegrationId };
  }, [tasyScheduleTypeIntegrationId]);

  const selectedEstablishments = watch("establishments");

  // Vazio = sem filtro (o back usa a restrição do grupo empresarial)
  const establishmentsId = useMemo(
    () => (selectedEstablishments ?? []).map((e) => e.id),
    [selectedEstablishments],
  );

  const scheduleParams = useMemo(() => {
    return {
      tasyScheduleTypeIntegrationId,
      establishmentsId: establishmentsId.length ? establishmentsId : undefined,
    };
  }, [tasyScheduleTypeIntegrationId, establishmentsId]);

  const fetchTasyScheduleRecords = useCallback(async () => {
    const { data, success } = await sendRequest(scheduleParams);
    if (data && success) {
      setOptionsScheduleRecords(
        data.map((d) => ({
          id: d.id,
          name:
            d.name +
            " - " +
            d.code +
            (d.establishmentName
              ? " - Estabelecimento: " + d.establishmentName
              : ""),
        })),
      );
    }
  }, [sendRequest, scheduleParams]);

  useEffect(() => {
    fetchTasyScheduleRecords();
  }, [fetchTasyScheduleRecords]);

  const fetchTasyPreferences = useCallback(async () => {
    if (!AUTOMATION_CODES_WITH_PREFERENCES.includes(String(automationCode)))
      return;
    const { data, success } = await calendarPreferenceRequest(params);
    if (data && success) {
      setOptionsPreferences(
        data.map((d) => ({
          id: d.identifier.toString(),
          name: d.preference,
        })),
      );
    }
  }, [calendarPreferenceRequest, params, automationCode]);

  useEffect(() => {
    fetchTasyPreferences();
  }, [fetchTasyPreferences]);

  const fetchIsRequiredCancellation = useCallback(async () => {
    const { data, success } = await isRequiredCancellationRequest({
      TicketAutomationRuleId: id,
      IsException: exceptionValue,
    });
    if (data && success) {
      setValue("isRequiredCancellation", data.isRequiredCancellation);
    }
  }, [isRequiredCancellationRequest, exceptionValue, id, setValue]);

  useEffect(() => {
    fetchIsRequiredCancellation();
  }, [fetchIsRequiredCancellation]);

  useEffect(() => {
    if (!showMissingStatus) {
      missingStatusFetchedRef.current = false;
      setMissingStatusOptions([]);
      setValue("missingStatus", null);
      return;
    }

    if (missingStatusFetchedRef.current) return;

    missingStatusFetchedRef.current = true;

    missingStatusRequest({
      Identifier: "TicketAutomationMissingStatus",
    }).then(({ data, success }) => {
      if (data && success) {
        setMissingStatusOptions(data as ISelectType[]);
      }
    });
  }, [
    showMissingStatus,
    missingStatusFetchedRef,
    missingStatusRequest,
    setValue,
  ]);

  return (
    <Box sx={{ pl: 2 }}>
      <Grid
        container
        spacing={2}
        sx={(t) => ({
          pr: 3,
          pl: 1,
          py: 3,
          border: `1px solid ${
            exceptionError ? t.palette.error.main : t.palette.divider
          }`,
          borderRadius: 2,
        })}
      >
        <Grid item sm={12}>
          <Typography fontWeight="bold">{t(translationPath + "if")}</Typography>
        </Grid>

        <Grid item sm={12} textAlign={"end"}>
          {automation &&
            ["12", "13", "14"].includes(String(automationCode)) && (
              <SwitchInput
                name="sendToConfimed"
                label={t(translationPath + "sendToConfimed")}
              />
            )}
          <SwitchInput name="enable" label={t(translationPath + "enable")} />
          {automation && !["30"].includes(String(automationCode)) && (
            <SwitchInput
              name="exception"
              label={t(translationPath + "exception")}
              disabled={idMessage ? true : false}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={9}>
          <FormInput
            size="small"
            name="name"
            fullWidth
            label={t(translationPath + "name") || "Name"}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormInput
            size="small"
            name="priority"
            fullWidth
            label={t(translationPath + "priority") || "Priority"}
          />
        </Grid>

        {automationCode &&
          ["16", "17", "18"].includes(String(automationCode)) && (
            <Grid item xs={12} sm={12}>
              <AutoComplete
                getOptionLabel={({ name }) => name}
                size="small"
                label={t(translationPath + "schedule_reminder_filter_type")}
                name="scheduleReminderFilterType"
                request={listMainGenerics}
                params={{ Identifier: "ScheduleReminderFilterType" }}
              />
            </Grid>
          )}

        <Grid item xs={12} sm={4.5}>
          <DateInput
            name="initialDate"
            label={t(translationPath + "initial_date")}
          />
        </Grid>

        <Grid item xs={12} sm={4.5}>
          <DateInput name="endDate" label={t(translationPath + "end_date")} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormInput
            type="number"
            size="small"
            name="qtyMessageSendingAttempts"
            fullWidth
            label={
              t(translationPath + "qtyMessageSendingAttempts") ||
              "qtyMessageSendingAttempts"
            }
            onBlur={() => {
              const current = getValues("qtyMessageSendingAttempts");
              const normalized = normalizeNumber(current, 0, 2147483647);
              setValue("qtyMessageSendingAttempts", String(normalized));
            }}
          />
        </Grid>

        {exceptionError && (
          <Grid item xs={12} sm={12}>
            <Typography color="error" variant="body2">
              {t(`${translationPath}warning`)}
            </Typography>
          </Grid>
        )}

        {automation &&
          AUTOMATION_CODES_WITH_ESTABLISHMENTS_AND_INSURANCES.includes(
            String(automationCode),
          ) && <SelectEstablishment automationCode={String(automationCode)} />}

        {automation && ["9", "10"].includes(String(automationCode)) && (
          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="qtyPacientSent"
              fullWidth
              label={
                t(translationPath + "qty_pacient_sent") ||
                "Number of patients to send"
              }
            />
          </Grid>
        )}

        {automation &&
          AUTOMATION_CODES_WITH_ESTABLISHMENTS_AND_INSURANCES.includes(
            String(automationCode),
          ) && (
            <Grid item xs={12} sm={12}>
              <AutoCompleteMulti
                name="tasyHealthInsurances"
                label={t(translationPath + "schedule_health_insurances")}
                getOptionLabel={(option) => option.name}
                size="small"
                fullWidth
                request={tasyHealthInsurancesService.list}
              />
            </Grid>
          )}

        {automationCode &&
          AUTOMATION_CODES_WITH_SCHEDULE_RECORDS.includes(
            String(automationCode),
          ) && (
            <Grid item xs={12} sm={12}>
              <AutoCompleteMulti
                params={scheduleParams}
                fullWidth
                options={optionsScheduleRecords}
                name="tasyScheduleRecords"
                label={t(translationPath + "schedule_records")}
                getOptionLabel={(option) => option.name}
                size="small"
                keepOpenOnSelect={true}
              />
            </Grid>
          )}
        {automationCode &&
          AUTOMATION_CODES_WITH_PREFERENCES.includes(
            String(automationCode),
          ) && (
            <Grid item xs={12} sm={12}>
              <AutoCompleteMulti
                params={params}
                fullWidth
                options={optionsPreferences}
                name="calendarPreferences"
                label={t(translationPath + "preference")}
                getOptionLabel={(option) => option.name}
                size="small"
              />
            </Grid>
          )}

        {automationCode &&
          AUTOMATION_CODES_WITH_CLASSIFICATIONS.includes(
            String(automationCode),
          ) && (
            <Grid item xs={12} sm={12}>
              <AutoCompleteMulti
                params={scheduleParams}
                name="tasyScheduleClassifications"
                label={t(translationPath + "schedule_classifications")}
                getOptionLabel={(option) => option.name}
                size="small"
                fullWidth
                request={tasyScheduleClassificationsService.list}
              />
            </Grid>
          )}

        {automationCode &&
          ["3", "27", "35"].includes(String(automationCode)) && (
            <Grid item xs={12} sm={12}>
              <AutoCompleteMulti
                name="tasyScheduleTypes"
                label={t(translationPath + "schedule_types")}
                getOptionLabel={(option) => option.name}
                size="small"
                fullWidth
                request={tasyScheduleTypesService.list}
              />
            </Grid>
          )}

        {showTasyNpsSatisfactionSurveyFields && (
          <Grid item xs={12} sm={12}>
            <FormInput
              size="small"
              name="indicationSequence"
              fullWidth
              label={t(translationPath + "indicationSequence")}
              helperText={t(translationPath + "indicationSequenceHelper")}
            />
          </Grid>
        )}

        <OriginsField />
        <SchedulingMethodField />
        <ServiceClassificationField />
        <ServiceSectorField />
        <ServiceTypeField />
        <ServiceComplaintField />
        <GroupingIdentifierField />
        <IntegrationTypeField />
        <InternalProcedureField />
        <IndividualIdentifierField />
        <TasyAuthField />
        <TasyCalendarStatusesField />
        <SurgeryTypeField />
        <SurgeryPorteField />
        <SurgeryCareCharacterField />
        <SurgeryServiceSectorField />
        <SurgeryHealthInsuranceField />

        {showMissingStatus && (
          <Grid item xs={12} sm={12}>
            <RadioGroupInput
              name="missingStatus"
              groupLabel={t(translationPath + "missing_status")}
              disableBorder
              radios={missingStatusOptions.map((option) => ({
                label: option.name,
                value: option,
              }))}
              valueField="id"
            />
          </Grid>
        )}
      </Grid>

      <Grid
        container
        spacing={2}
        sx={(t) => ({
          mt: 5,
          pr: 3,
          pl: 1,
          py: 3,
          border: `1px solid ${t.palette.divider}`,
          borderRadius: 2,
        })}
      >
        <Grid item sm={12}>
          <Typography fontWeight="bold">
            {t(translationPath + "then")}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={2}>
          <SwitchInput name="send" label={t(translationPath + "send")} />
        </Grid>

        {automationCode &&
          ["1", "5", "11", "16", "17", "18", "34"].includes(
            String(automationCode),
          ) && (
            <Grid item xs={12} sm={12}>
              <AutoComplete
                getOptionLabel={({ name }) => name}
                size="small"
                label={t(translationPath + "rounded_schedules_date")}
                name="ticketAutRoundSchedulesDate"
                request={listMainGenerics}
                params={{ Identifier: "TicketAutomationRoundSchedulesDate" }}
              />
            </Grid>
          )}

        {automationCode &&
          ["1", "5", "11", "34"].includes(String(automationCode)) && (
            <Grid item xs={12} sm={12}>
              <AutoComplete
                getOptionLabel={({ name }) => name}
                size="small"
                label={t(translationPath + "arrival_time_rule")}
                name="appointmentArrivalRule"
                request={listAppointmentArrivalRules}
              />
            </Grid>
          )}

        <SelectEvaluationForm />

        {showTasyNpsSatisfactionSurveyFields && (
          <Grid item xs={12} sm={12}>
            <SwitchInput
              name="usePatientEncounterInfoPhone"
              label={t(translationPath + "usePatientEncounterInfoPhone")}
            />
          </Grid>
        )}

        {automationCode === "30" && (
          <Grid item xs={12} sm={8}>
            <AutoCompleteMulti
              getOptionLabel={({ name }) => name}
              size="small"
              label={t(translationPath + "e_sign_rules")}
              name="esignRules"
              request={eSignRuleList}
              keepOpenOnSelect
            />
          </Grid>
        )}
      </Grid>

      {automation &&
        !["2", "6", "3", "30"].includes(String(automationCode)) &&
        !exceptionValue && (
          <Actions
            automationCode={automationCode}
            automation={automation}
            ruleTemplate={ruleTemplate}
          />
        )}
    </Box>
  );
});
